import { supabase } from './supabase';

/**
 * Add an activity to the cms_activities table
 * @param {string} title - The activity title (e.g., "New School Registered")
 * @param {string} details - Additional details (optional)
 * @param {string} addedBy - Who added this activity (optional, defaults to "System")
 * @param {string|null} schoolId - The school ID for school-specific activities (optional, null for admin activities)
 * @returns {Promise<boolean>} - Success status
 */
export const logActivity = async (title, details = '', addedBy = 'System', schoolId = null) => {
  try {
    const activityData = {
      title,
      details,
      added_by: addedBy,
      added_at: new Date().toISOString()
    };

    // Only include school_id if it's provided (for school-specific activities)
    if (schoolId !== null && schoolId !== undefined) {
      activityData.school_id = schoolId;
    }

    const { error } = await supabase
      .from('cms_activities')
      .insert([activityData]);

    if (error) return false;
    return true;
  } catch (err) {
    return false;
  }
};

/**
 * Log school-related activities
 */
export const logSchoolActivity = {
  created: (schoolName) => logActivity(
    `New School Registered: ${schoolName}`,
    `School "${schoolName}" has been successfully registered in the system.`,
    'System'
  ),
  updated: (schoolName) => logActivity(
    `School Updated: ${schoolName}`,
    `School "${schoolName}" information has been updated.`,
    'System'
  ),
  deleted: (schoolName) => logActivity(
    `School Deleted: ${schoolName}`,
    `School "${schoolName}" has been removed from the system.`,
    'System'
  )
};

/**
 * Log user-related activities
 */
export const logUserActivity = {
  created: (userName, schoolName, schoolId = null) => logActivity(
    `New User Added: ${userName}`,
    `User "${userName}" has been added to "${schoolName || 'the system'}".`,
    'System',
    schoolId
  ),
  updated: (userName, schoolId = null) => logActivity(
    `User Updated: ${userName}`,
    `User "${userName}" information has been updated.`,
    'System',
    schoolId
  ),
  deleted: (userName, schoolId = null) => logActivity(
    `User Deleted: ${userName}`,
    `User "${userName}" has been removed from the system.`,
    'System',
    schoolId
  )
};

/**
 * Log provider-related activities
 */
export const logProviderActivity = {
  created: (providerName) => logActivity(
    `New Provider Added: ${providerName}`,
    `Learning provider "${providerName}" has been added to the system.`,
    'System'
  ),
  updated: (providerName) => logActivity(
    `Provider Updated: ${providerName}`,
    `Provider "${providerName}" information has been updated.`,
    'System'
  ),
  deleted: (providerName) => logActivity(
    `Provider Deleted: ${providerName}`,
    `Provider "${providerName}" has been removed from the system.`,
    'System'
  )
};
